/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot, getOfferInboxData } from '../../../state/ducks/App/App-Selectors';
import {
    getIsAuthenticatedStatus, getProfileInfo, getUserEmailId, getUserType,
} from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import useBrowserUUID from '../useBrowserUUID';
import { getFeatureFlag } from '../../../state/ducks/App/ducks/Config/Config-Selectors';
import findOfferInboxQuery from '../../gql/queries/findOfferInboxQuery';
import { getPassportData, getPassportSubscriptionStatus } from '../../../state/ducks/Passport/Passport-Selectors';
import validatePassport from '../validatePassport';
import { getTalonOneCurrentBalance } from '../../../state/ducks/Member/ducks/TalonOneRewards/TalonOneRewards-Selectors';

/**
 * @description - hook to fetch Salesforce Inbox offers
 * @param {object} apolloOptions - overrides | additional options to pass to useQuery API
 * @param {string} queryName - GraphQL query alias
 * @param {boolean} featureFlag - feature flag to enable/disable call
 * @param {boolean} isSessionHasAccount - add hasAccountCookie attribute
 * @returns {object} - { loading, error, data, variables }
 */

const useSalesforceOfferInboxQuery = ({
    apolloOptions = {},
    queryName = 'OfferInbox',
    featureFlag = true,
    isSessionHasAccount = false, // needed for checking if guest user has account
    optInHoldoutParticipation = false,
    resolveIdentity = false,
}) => {
    const isBot = useSelector(getIsBot);
    const isSalesForceRecsEnabled = useSelector(getFeatureFlag('is-salesforce-recs-enabled'));
    const offerInbox = useSelector(getOfferInboxData);// update this
    const rewardsBalance = useSelector(getTalonOneCurrentBalance);

    if (isBot || !featureFlag || !isSalesForceRecsEnabled) {
        return {
            loading: false,
            error: false,
            data: null,
            variables: { skip: false },
        };
    }

    const query = findOfferInboxQuery(queryName);
    const brand = useSelector(getBrandName) || '1800flowers';
    const emailId = useSelector(getUserEmailId);
    const browserUUID = useBrowserUUID();
    const isPersonalizationHoldoutEnabled = optInHoldoutParticipation
        ? useSelector(getFeatureFlag('is-personalization-holdout-enabled'))
        : optInHoldoutParticipation;

    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);

    let sessionCustomerType;
    if ((isPassportMember && isAuthenticated) || isPassportAdded) {
        sessionCustomerType = 'P'; // Passport members > Registered users
    } else if (isAuthenticated) {
        sessionCustomerType = 'R'; // Registered user, but not a Passport member
    } else {
        sessionCustomerType = 'G'; // Guest
    }

    const user = { anonymousId: browserUUID };
    if (offerInbox?.email || emailId) {
        user.emailId = offerInbox?.email || emailId;
    }
    if (offerInbox?.phone) {
        user.phone = offerInbox?.phone;
    }

    const userType = useSelector(getUserType);
    const userAttributes = [];
    let sessionOGOptin = `${offerInbox?.offerGatewayOptin}`;
    let sessionOGLoggedIn = 'false';
    let sessionOGAccountCreation = 'false';
    const userInfo = useSelector(getProfileInfo);

    userAttributes.push({ name: 'sessionLoginState', value: userType === 'R' || userType === 'P' ? 'true' : 'false' });
    userAttributes.push({ name: 'sessionCustomerType', value: sessionCustomerType });

    if (userInfo?.offerGatewayOptin) {
        sessionOGOptin = userInfo.offerGatewayOptin === 'true';
        sessionOGLoggedIn = `${userInfo?.loginsCount > 1}`;
        sessionOGAccountCreation = `${userInfo?.loginsCount === 1}`;
    }

    if (isAuthenticated) {
        userAttributes.push({ name: 'HasTalonCredit', value: rewardsBalance > 0 ? 'true' : 'false' });
    }

    userAttributes.push({ name: 'sessionOGOptIn', value: `${sessionOGOptin}` });
    userAttributes.push({ name: 'sessionOGLoggedIn', value: `${sessionOGLoggedIn}` });
    userAttributes.push({ name: 'sessionOGAccountCreation', value: `${sessionOGAccountCreation}` });

    userAttributes.push({ name: 'sessionOGOptInSMS', value: `${offerInbox?.offerGatewayOptinSMS}` });
    userAttributes.push({ name: 'sessionOGOfferUnlockSMS', value: `${offerInbox?.offerUnlockedSMS}` });

    if (isSessionHasAccount) {
        userAttributes.push({ name: 'hasAccountCookie', value: 'true' });
    }

    const jwt = useGetAccessTokenSafely();
    const skip = Boolean(!jwt || !browserUUID);

    return useQuery(query, {
        variables: skip
            ? { skip }
            : {
                brand,
                environment: GRAPHQL_ENV,
                isHoldoutEligible: isPersonalizationHoldoutEnabled,
                resolveIdentity,
                user,
                userAttributes,
            },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useSalesforceOfferInboxQuery;
